import { ParsedCSVData, ValidationError } from '@/types/upload';
import { type CsvRow, toCsvRow } from '@/utils/csvRow';

/**
 * 来店くじ設定 CSV のクライアント側バリデーション。
 *
 * S3 へ presigned URL でアップロードする前に、Book / Product / Menu / Corner / Special / Coupon と
 * 同様に全ルールをクライアントで検証してプレビュー可否を判定する。サーバー（ProcessUpload Lambda /
 * LotoUploadService）でも同一ルールを再検証する（多層防御）。
 *
 * Coupon と違い **ブロック形式**（くじ情報行 1 行 + 確率情報行 N 行 = 1 設定）で、Menu の
 * MENU/BLOCK/ITEM 階層に近い。行種別は **`loto_name` 列が埋まっているか**で判定する。
 * くじは単一運用が構造上の前提で、くじ ID（loto_id）は存在しない（DB の pk は固定の `LOTO`）。
 *
 * 仕様の正本: docs/features/loto.md「CSV フォーマット」「CSV バリデーションルール」。
 *
 * 権威はサーバー（loto_service.py）側にあり、ここはアップロード前の UX 向け事前検証。
 * 仕様変更時は docs/features/loto.md を基点に loto_service.py と本ファイルの両方を更新すること。
 *
 * ## ここで検証できないもの
 * **V-15（既存 DB 上の設定との期間重複）は登録済みデータの読み取りが必要なため判定できない。**
 * 専用のプレビュー API を設けない方針（ADR-005）のため、サーバー側でのみ検証され、
 * 抵触した場合はアップロード後に `FAILED` として返る。
 *
 * ## 警告（`W-`）
 * `validateLotoCsvWarnings` が返す。**取込を妨げない**が、運営の意図と違う可能性が高いものを
 * 拾う。主眼は原資事故の防止で、`chance_rate` は桁を誤っても合計 1,000,000 が成立してしまい
 * エラーでは捕まらないため、期待値の急変（W-01）を人間の目に留める。
 */

const COLUMNS = [
  // くじ情報行が埋める列（1〜6 列目）
  'loto_name',
  'start_at',
  'end_at',
  'frequency',
  'times',
  'loto_image',
  // 確率情報行が埋める列（7〜12 列目）
  'rate_name',
  'title',
  'chance_rate',
  'win_point',
  'rate_image',
  'rate_description',
] as const;

type LotoColumn = (typeof COLUMNS)[number];
type LotoRowData = CsvRow<typeof COLUMNS>;

const CONFIG_COLUMNS: readonly LotoColumn[] = COLUMNS.slice(0, 6);
const RATE_COLUMNS: readonly LotoColumn[] = COLUMNS.slice(6);

/** くじ情報行の必須列（`loto_image` のみ任意）。V-02。 */
const CONFIG_REQUIRED: readonly LotoColumn[] = [
  'loto_name',
  'start_at',
  'end_at',
  'frequency',
  'times',
];

/** 確率情報行の必須列（`title` / `rate_image` は任意）。V-02。 */
const RATE_REQUIRED: readonly LotoColumn[] = [
  'rate_name',
  'chance_rate',
  'win_point',
  'rate_description',
];

/** 当選確率の合計（100 万分率 / ppm）。V-09。 */
export const CHANCE_RATE_TOTAL = 1_000_000;
/** ポイント → eコインの換算（1:1）。 */
export const POINT_PER_COIN = 1;
/** 適用期間の上限日数（超えると W-06）。 */
const LONG_PERIOD_DAYS = 366;
/** 1 ファイルの設定数上限（サーバーは 1 回の TransactWriteItems（100 項目）で原子的に保存する）。 */
export const MAX_CONFIGS_PER_FILE = 100;

/** 文字数上限（schemas/components/entities/loto.yaml の maxLength と同値）。 */
const MAX_LENGTHS = {
  loto_name: 64,
  loto_image: 256,
  rate_name: 64,
  title: 128,
  rate_image: 256,
  rate_description: 512,
} as const;

// CSV の日付入力。区切りは `-` / `/` のどちらか（バックリファレンスで統一を強制。
// `2026-9/1` のような混在は入力ミスとして弾く）、月日は 1〜2 桁を受理する
// （Book 等と同じ柔軟受理。SBI #859。V-03）。比較・重複判定はサーバー
// （loto_service.py）と同様に YYYY-MM-DD へ正規化した値で行う。
const DATE_RE = /^(\d{4})([-/])(\d{1,2})\2(\d{1,2})$/;
const SLUG_RE = /^[a-zA-Z0-9_-]+$/;
const INT_RE = /^-?\d+$/;
const DATE_FORMAT_HINT = 'の日付形式が不正です（例: 2026-09-01 / 2026/9/1）';

const err = (row: number, column: string, value: string, error: string): ValidationError => ({
  row,
  column,
  value,
  error,
});

/** CSV の日付入力（`-` / `/` 区切り・月日 1〜2 桁）を UTC 基準の Date に変換する（実在しない日付は null）。 */
const parseDate = (value: string): Date | null => {
  const m = DATE_RE.exec(value);
  if (!m) return null;
  // m[2] は区切り文字（バックリファレンス用）のため月日は m[3] / m[4]
  const [year, month, day] = [Number(m[1]), Number(m[3]), Number(m[4])];
  const d = new Date(Date.UTC(year, month - 1, day));
  // 2026-02-30 のような繰り上がりを弾く
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day
    ? d
    : null;
};

/** 日付入力を `YYYY-MM-DD` へ正規化する（解釈できない値は入力のまま。エラーは V-03 が担う）。 */
const normalizeDate = (value: string): string =>
  parseDate(value)?.toISOString().slice(0, 10) ?? value;

const DAY_MS = 24 * 60 * 60 * 1000;
const diffDays = (from: Date, to: Date): number =>
  Math.round((to.getTime() - from.getTime()) / DAY_MS);

/** 整数として解釈する（できなければ null）。 */
const toInt = (value: string): number | null => {
  if (!INT_RE.test(value)) return null;
  return Number.parseInt(value, 10);
};

interface LotoRateData {
  rate_name: string;
  chance_rate: number | null;
  win_point: number | null;
}

interface LotoBlock {
  row: number;
  start_at: string;
  end_at: string;
  rates: LotoRateData[];
  rateRows: number[];
}

const blockLabel = (b: LotoBlock): string => b.start_at;

/**
 * 説明行（2 行目）の位置にデータ行が来ていないかを判定する。
 * くじ情報行なら start_at 列が日付として、確率情報行なら chance_rate 列が
 * 整数として解釈できる。説明行の同列は日本語の説明文のためどちらも解釈できない。
 */
const looksLikeDataRow = (cells: readonly string[]): boolean => {
  const startAt = (cells[COLUMNS.indexOf('start_at')] ?? '').trim();
  const chanceRate = (cells[COLUMNS.indexOf('chance_rate')] ?? '').trim();
  return parseDate(startAt) !== null || toInt(chanceRate) !== null;
};

/** くじ情報行の検証（V-02 / V-03 / V-04 / V-06 / V-12）。 */
const validateConfigRow = (rowNum: number, d: LotoRowData, errors: ValidationError[]): void => {
  CONFIG_REQUIRED.forEach((col) => {
    if (!d[col]) errors.push(err(rowNum, col, '', `${col} は必須です`));
  });

  // V-06: 確率情報の列が空であること
  RATE_COLUMNS.forEach((col) => {
    if (d[col]) {
      errors.push(err(rowNum, col, d[col], 'くじ情報行に確率情報が入力されています'));
    }
  });

  if (d.loto_name && !SLUG_RE.test(d.loto_name)) {
    errors.push(
      err(
        rowNum,
        'loto_name',
        d.loto_name,
        'loto_name は半角英数・ハイフン・アンダースコアのみです'
      )
    );
  }

  // V-03: 日付形式
  (['start_at', 'end_at'] as const).forEach((col) => {
    if (d[col] && !parseDate(d[col])) {
      errors.push(err(rowNum, col, d[col], `${col} ${DATE_FORMAT_HINT}`));
    }
  });

  // V-04 / V-12: 整数かつ 1 以上
  (['frequency', 'times'] as const).forEach((col) => {
    if (!d[col]) return;
    const value = toInt(d[col]);
    if (value === null) {
      errors.push(err(rowNum, col, d[col], `${col} の数値が不正です`));
    } else if (value < 1) {
      errors.push(err(rowNum, col, d[col], `${col} は 1 以上にしてください`));
    }
  });

  // 文字数上限（サーバー側 Pydantic と同値）
  (['loto_name', 'loto_image'] as const).forEach((col) => {
    if (d[col].length > MAX_LENGTHS[col]) {
      errors.push(
        err(rowNum, col, d[col], `${col} は ${MAX_LENGTHS[col]} 文字以内で入力してください`)
      );
    }
  });
};

/** 確率情報行の検証（V-02 / V-04 / V-06）。 */
const validateRateRow = (rowNum: number, d: LotoRowData, errors: ValidationError[]): void => {
  RATE_REQUIRED.forEach((col) => {
    if (!d[col]) errors.push(err(rowNum, col, '', `${col} は必須です`));
  });

  // V-06: くじ情報の列が空であること（loto_name は行種別の判定に使うため対象外）
  CONFIG_COLUMNS.forEach((col) => {
    if (col !== 'loto_name' && d[col]) {
      errors.push(err(rowNum, col, d[col], '確率情報行にくじ情報が入力されています'));
    }
  });

  if (d.rate_name && !SLUG_RE.test(d.rate_name)) {
    errors.push(
      err(
        rowNum,
        'rate_name',
        d.rate_name,
        'rate_name は半角英数・ハイフン・アンダースコアのみです'
      )
    );
  }

  // V-04: 整数かつ 0 以上
  (['chance_rate', 'win_point'] as const).forEach((col) => {
    if (!d[col]) return;
    const value = toInt(d[col]);
    if (value === null) {
      errors.push(err(rowNum, col, d[col], `${col} の数値が不正です`));
    } else if (value < 0) {
      errors.push(err(rowNum, col, d[col], `${col} は 0 以上にしてください`));
    }
  });

  // 文字数上限（サーバー側 Pydantic と同値）
  (['rate_name', 'title', 'rate_image', 'rate_description'] as const).forEach((col) => {
    if (d[col].length > MAX_LENGTHS[col]) {
      errors.push(
        err(rowNum, col, d[col], `${col} は ${MAX_LENGTHS[col]} 文字以内で入力してください`)
      );
    }
  });
};

/**
 * データ行をブロックへ分割する。行種別は `loto_name` 列が埋まっているかで判定する。
 * 行単位の検証（V-02〜V-07）もここで行う。
 */
const splitBlocks = (data: ParsedCSVData, errors: ValidationError[]): LotoBlock[] => {
  const blocks: LotoBlock[] = [];
  let current: LotoBlock | null = null;

  data.rows.forEach((row, idx) => {
    // parseCSV が 2 行目（日本語説明）をスキップ済みのため rows[0] は実ファイル 3 行目に相当する。
    const rowNum = idx + 3;

    // V-07: 空行を含まない
    if (!row.some((c) => c.trim())) {
      errors.push(err(rowNum, '行', '', '空行は削除してください'));
      return;
    }

    if (row.length !== COLUMNS.length) {
      errors.push(
        err(
          rowNum,
          '列数',
          row.join(','),
          `列数が不正です（${row.length} 列、期待 ${COLUMNS.length} 列）`
        )
      );
      return;
    }

    const d = toCsvRow(COLUMNS, row);

    if (d.loto_name) {
      validateConfigRow(rowNum, d, errors);
      current = {
        row: rowNum,
        // `-` / `/` 混在入力を YYYY-MM-DD へ正規化（V-13 の重複判定・ラベルを
        // サーバー（loto_service.py）と一貫させる。SBI #859）
        start_at: normalizeDate(d.start_at),
        end_at: normalizeDate(d.end_at),
        rates: [],
        rateRows: [],
      };
      blocks.push(current);
      return;
    }

    // V-05: 先頭ブロックの開始前に確率情報行がない
    if (current === null) {
      errors.push(err(rowNum, 'loto_name', '', 'くじ情報行より前に確率情報行があります'));
      return;
    }

    validateRateRow(rowNum, d, errors);
    current.rates.push({
      rate_name: d.rate_name,
      chance_rate: toInt(d.chance_rate),
      win_point: toInt(d.win_point),
    });
    current.rateRows.push(rowNum);
  });

  return blocks;
};

/** 設定単位の検証（V-08 / V-09 / V-10 / V-11）。 */
const validateBlock = (b: LotoBlock, errors: ValidationError[]): void => {
  const label = blockLabel(b);

  // V-11: 確率情報行が 1 件以上続いている
  if (b.rates.length === 0) {
    errors.push(err(b.row, 'rate_name', '', 'くじ情報行に対する確率情報行がありません'));
    return;
  }

  // V-08: start_at <= end_at
  const start = parseDate(b.start_at);
  const end = parseDate(b.end_at);
  if (start && end && start > end) {
    errors.push(err(b.row, 'end_at', b.end_at, `設定 ${label}: 開始日が終了日より後です`));
  }

  // V-09: chance_rate の合計がちょうど 1,000,000。
  // 数値として不正なレートを含む場合は行エラーが既に出ているため、
  // 誤解を招く二次エラー（合計 0 等）を出さずスキップする
  const chanceRates = b.rates.map((r) => r.chance_rate);
  if (chanceRates.every((r): r is number => r !== null)) {
    const total = chanceRates.reduce((acc, r) => acc + r, 0);
    if (total !== CHANCE_RATE_TOTAL) {
      errors.push(
        err(
          b.row,
          'chance_rate',
          String(total),
          `設定 ${label}: 確率の合計が ${total.toLocaleString()} です。${CHANCE_RATE_TOTAL.toLocaleString()} にしてください`
        )
      );
    }
  }

  // V-10: 同一設定内で rate_name が重複しない
  const seen = new Set<string>();
  const dup = new Set<string>();
  b.rates.forEach((r) => {
    if (seen.has(r.rate_name)) dup.add(r.rate_name);
    seen.add(r.rate_name);
  });
  if (dup.size > 0) {
    errors.push(
      err(b.row, 'rate_name', [...dup].join(','), `設定 ${label}: 当選区分名が重複しています`)
    );
  }
};

/** CSV 全体の横断検証（V-13 / V-14）。運用するくじは 1 本のため start_at だけで識別する。 */
const validateCsvWide = (blocks: LotoBlock[], errors: ValidationError[]): void => {
  const seen = new Map<string, number>();
  const periods: { start: Date; end: Date; row: number; label: string }[] = [];

  blocks.forEach((b) => {
    // V-13: 同一 CSV 内で start_at が重複しない
    const key = b.start_at;
    const prev = seen.get(key);
    if (prev !== undefined) {
      errors.push(
        err(
          b.row,
          'start_at',
          b.start_at,
          `設定 ${blockLabel(b)} が重複して定義されています（${prev} 行目と重複）`
        )
      );
      return;
    }
    seen.set(key, b.row);

    const start = parseDate(b.start_at);
    const end = parseDate(b.end_at);
    if (start && end && start <= end) {
      periods.push({ start, end, row: b.row, label: blockLabel(b) });
    }
  });

  // V-14: CSV 内で適用期間が重複しない
  const ordered = [...periods].sort((a, z) => a.start.getTime() - z.start.getTime());
  ordered.forEach((cur, i) => {
    const next = ordered[i + 1];
    if (next && next.start <= cur.end) {
      errors.push(
        err(
          next.row,
          'start_at',
          next.start.toISOString().slice(0, 10),
          `設定 ${cur.label} と ${next.label} の期間が重複しています`
        )
      );
    }
  });
};

/**
 * エラー（`V-`）の検証。1 件でもあればアップロードできない（All-or-Nothing）。
 */
export const validateLotoCsv = (data: ParsedCSVData): ValidationError[] => {
  // V-01: ヘッダー（順序込み完全一致）
  const headerMatches =
    data.headers.length === COLUMNS.length && COLUMNS.every((c, i) => data.headers[i] === c);
  if (!headerMatches) {
    return [
      err(
        1,
        'ヘッダー',
        data.headers.join(','),
        `CSVヘッダーが不正です。期待: ${COLUMNS.join(',')}`
      ),
    ];
  }

  // 説明行（2 行目）の存在検証。説明行を削除した CSV は先頭のデータ行が説明行として
  // 読み飛ばされ、原因の分からないエラー（V-05 等）になるため明示的に弾く。
  if (looksLikeDataRow(data.displayHeaders)) {
    return [
      err(
        2,
        '説明行',
        data.displayHeaders.join(','),
        '説明行（2 行目）がありません（2 行目がデータ行のようです）'
      ),
    ];
  }

  if (data.rows.length === 0) {
    return [
      err(1, 'データ', '', 'データ行がありません（1=カラム名 / 2=日本語説明 / 3 行目以降=データ）'),
    ];
  }

  const errors: ValidationError[] = [];
  const blocks = splitBlocks(data, errors);

  // 保存は 1 回の TransactWriteItems（100 項目上限）で原子的に行うため、設定数を制限する
  if (blocks.length > MAX_CONFIGS_PER_FILE) {
    errors.push(
      err(
        3,
        'loto_name',
        String(blocks.length),
        `設定は 1 ファイルにつき最大 ${MAX_CONFIGS_PER_FILE} 件です（${blocks.length} 件）`
      )
    );
  }

  /* v8 ignore next 3 -- 到達不能な防御的分岐（rows が非空なら splitBlocks は各行で必ずブロックかエラーを生む） */
  if (blocks.length === 0 && errors.length === 0) {
    return [err(3, 'loto_name', '', 'くじ情報行がありません')];
  }

  blocks.forEach((b) => validateBlock(b, errors));
  validateCsvWide(blocks, errors);

  return errors;
};

/**
 * 1 回あたりの期待獲得eコイン。`sum(chance_rate * win_point) / 1,000,000`（換算 1:1）。
 *
 * 確率が ppm 単位のため桁の誤りが目視で気づきにくい。期待値を出して比較することで
 * 「合計 1,000,000 は成立しているが原資が 10 倍になる」変更を検知する（W-01）。
 */
export const expectedCoinPerDraw = (rates: readonly LotoRateData[]): number => {
  const points = rates.reduce((acc, r) => acc + (r.chance_rate ?? 0) * (r.win_point ?? 0), 0);
  return points / CHANCE_RATE_TOTAL / POINT_PER_COIN;
};

/**
 * 警告（`W-`）の検証。**取込を妨げない**が、運営の意図と違う可能性が高いものを拾う。
 *
 * エラーがある状態では呼ばない（`validateLotoCsv` が空を返したときのみ評価する）。
 * `today` は W-03 / W-04 の判定に使う（テスト用に注入可能）。
 */
export const validateLotoCsvWarnings = (
  data: ParsedCSVData,
  today: Date = new Date()
): ValidationError[] => {
  const warnings: ValidationError[] = [];
  const ignored: ValidationError[] = [];
  const blocks = splitBlocks(data, ignored);
  const todayUtc = parseDate(today.toISOString().slice(0, 10));

  let prev: { label: string; expected: number } | null = null;

  blocks.forEach((b) => {
    const label = blockLabel(b);
    const start = parseDate(b.start_at);
    const end = parseDate(b.end_at);
    const expected = expectedCoinPerDraw(b.rates);

    // W-01: 期待獲得eコインが直前ブロックの 2 倍以上 / 半分以下
    if (
      prev &&
      prev.expected > 0 &&
      (expected >= prev.expected * 2 || expected <= prev.expected / 2)
    ) {
      warnings.push(
        err(
          b.row,
          'chance_rate',
          expected.toFixed(4),
          `W-01 設定 ${label} の期待獲得eコインが ${expected.toFixed(4)} です（設定 ${prev.label} は ${prev.expected.toFixed(4)}）。確率の桁を確認してください`
        )
      );
    }

    if (start && end) {
      // W-03: start_at が今日より過去
      if (todayUtc && start < todayUtc) {
        warnings.push(
          err(b.row, 'start_at', b.start_at, `W-03 設定 ${label} の適用開始日は過去日です`)
        );
      }
      // W-04: 適用中の設定を上書きしている
      if (todayUtc && start <= todayUtc && todayUtc <= end) {
        warnings.push(
          err(
            b.row,
            'start_at',
            b.start_at,
            `W-04 設定 ${label} は現在適用中です。上書きすると最大 60 秒後に確率が切り替わります`
          )
        );
      }
      // W-06: 適用期間が 366 日以上
      const days = diffDays(start, end) + 1;
      if (days >= LONG_PERIOD_DAYS) {
        warnings.push(
          err(
            b.row,
            'end_at',
            b.end_at,
            `W-06 設定 ${label} の適用期間が ${days} 日です。終了日を確認してください`
          )
        );
      }
    }

    b.rates.forEach((r, i) => {
      /* v8 ignore next -- rates と rateRows は splitBlocks で同時に push しており長さが常に一致する */
      const rateRow = b.rateRows[i] ?? b.row;
      // W-05: chance_rate = 0 のレートがある
      if (r.chance_rate === 0) {
        warnings.push(
          err(
            rateRow,
            'chance_rate',
            '0',
            `W-05 設定 ${label} の当選区分 ${r.rate_name} は当選確率が 0 です`
          )
        );
      }
      // W-07: win_point = 0 のレートがある
      if (r.win_point === 0) {
        warnings.push(
          err(
            rateRow,
            'win_point',
            '0',
            `W-07 設定 ${label} の当選区分 ${r.rate_name} は獲得ポイントが 0 です（ハズレ扱いになります）`
          )
        );
      }
    });

    prev = { label, expected };
  });

  // W-02: CSV 内の設定間に期間の空白がある
  const orderedBlocks = blocks
    .map((b) => ({ b, start: parseDate(b.start_at), end: parseDate(b.end_at) }))
    .filter(
      (x): x is { b: LotoBlock; start: Date; end: Date } => x.start !== null && x.end !== null
    )
    .sort((a, z) => a.start.getTime() - z.start.getTime());
  orderedBlocks.forEach((cur, i) => {
    const next = orderedBlocks[i + 1];
    if (!next) return;
    const gap = diffDays(cur.end, next.start) - 1;
    if (gap > 0) {
      warnings.push(
        err(
          next.b.row,
          'start_at',
          next.b.start_at,
          `W-02 設定 ${blockLabel(cur.b)}（〜${cur.b.end_at}）と ${blockLabel(next.b)}（${next.b.start_at}〜）の間に ${gap} 日の空白があります。この期間はくじ未開催になります`
        )
      );
    }
  });

  return warnings;
};
